import { useState } from 'react';
import { Box, Chip, Divider, Link, Popover, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useDatastore } from '../../hooks';
import { setForceResyncOnBoot } from '../../common/helperFunctions/localStorage';
import SyncDiagnosticsPanel from '../../pages/settings/SyncDiagnosticsPanel';
import { ConfirmationDialog } from '../modals/ConfirmDialog';

interface SyncStatusPopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

/**
 * Inline popover shown by clicking the header sync-status button
 * (FullSyncIndicator). Lets a user check per-model sync status and force a
 * full re-sync from any page, without navigating to /settings.
 */
export default function SyncStatusPopover({ open, anchorEl, onClose }: SyncStatusPopoverProps) {
  const theme = useTheme();
  const { isEssentialReady = false, isDatastoreReady = false, isSyncing = false } = useDatastore();
  const [confirmResyncOpen, setConfirmResyncOpen] = useState(false);

  // Force a full re-sync. We do NOT stop()/clear() inline: while an initial sync is
  // in flight, DataStore.stop() can hang indefinitely, so the old await-chain never
  // reached reload (confirm appeared to "do nothing"). Instead flag a boot-time wipe
  // and reload immediately — the reload interrupts the current sync, and the provider
  // clears the store before DataStore.start() on the next boot for a true full re-sync.
  const handleResync = () => {
    setConfirmResyncOpen(false);
    setForceResyncOnBoot();
    window.location.reload();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
      <Box sx={{ width: 380, maxWidth: '90vw', p: 2 }}>
        <Stack spacing={2}>
          <Stack spacing={1}>
            <Typography variant="subtitle1" fontWeight="bold">
              Sync status
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                label={`Essential: ${isEssentialReady ? 'ready' : 'pending'}`}
                color={isEssentialReady ? 'success' : 'default'}
              />
              <Chip
                size="small"
                label={`Full sync: ${isDatastoreReady ? 'ready' : 'pending'}`}
                color={isDatastoreReady ? 'success' : 'default'}
              />
              {isSyncing && <Chip size="small" label="Syncing…" color="warning" />}
            </Stack>
          </Stack>

          <Divider />

          <Box sx={{ maxHeight: 320, overflowY: 'auto' }}>
            <SyncDiagnosticsPanel compact />
          </Box>

          <Divider />

          <Box sx={{ textAlign: 'center' }}>
            <Link
              component="button"
              type="button"
              underline="hover"
              onClick={() => setConfirmResyncOpen(true)}
              sx={{ fontSize: '0.8rem', color: theme.palette.text.secondary }}>
              Reload &amp; full re-sync now
            </Link>
          </Box>
        </Stack>
      </Box>
      <ConfirmationDialog
        open={confirmResyncOpen}
        onClose={() => setConfirmResyncOpen(false)}
        onConfirm={handleResync}
        title="Reload & full re-sync"
        content="This stops the current sync, re-downloads all data from scratch, and reloads the app. Any unsynced changes will be lost. Continue?"
      />
    </Popover>
  );
}
